import { useEffect, useRef, useState } from "react";
import api, { fmtDate, BACKEND } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Loading } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Camera, Building2, Phone, MapPin, CalendarDays, BadgeCheck } from "lucide-react";

const roleLabel = { owner: "Owner", staff: "Staff", staf: "Staff", kasir: "Kasir" };

function membership(dateStr) {
  if (!dateStr) return "-";
  const joined = new Date(dateStr);
  const days = Math.max(0, Math.floor((Date.now() - joined.getTime()) / 86400000));
  const years = Math.floor(days / 365);
  const months = Math.floor((days % 365) / 30);
  const parts = [];
  if (years) parts.push(`${years} tahun`);
  if (months) parts.push(`${months} bulan`);
  parts.push(`${days % 30} hari`);
  return parts.join(" ");
}

export default function Profile() {
  const { isPriv } = useAuth();
  const [me, setMe] = useState(null);
  const [branches, setBranches] = useState([]);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const fileRef = useRef();

  useEffect(() => {
    api.get("/auth/me").then(({ data }) => { setMe(data); setForm(data); }).catch(() => {});
    api.get("/branches").then(({ data }) => setBranches(data)).catch(() => {});
  }, []);

  const syncLocal = (patch) => {
    try { const u = JSON.parse(localStorage.getItem("user")) || {}; localStorage.setItem("user", JSON.stringify({ ...u, ...patch })); } catch {}
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.put("/profile", { image_url: data.url });
      setMe({ ...me, image_url: data.url });
      setForm({ ...form, image_url: data.url });
      syncLocal({ image_url: data.url });
      toast.success("Foto profil diperbarui");
    } catch { toast.error("Gagal unggah foto"); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = isPriv
        ? { name: form.name, role: form.role, phone: form.phone, address: form.address, join_date: form.join_date }
        : { phone: form.phone };
      const { data } = await api.put("/profile", payload);
      setMe(data); setForm(data);
      syncLocal({ name: data.name, role: data.role });
      toast.success("Profil disimpan");
    } catch { toast.error("Gagal menyimpan"); } finally { setSaving(false); }
  };

  if (!me) return <Loading />;
  const branchName = branches.find((b) => b.id === me.branch_id)?.name || "-";
  const img = me.image_url ? (me.image_url.startsWith("http") ? me.image_url : `${BACKEND}${me.image_url}`) : null;
  const joinDate = me.join_date || me.created_at;

  return (
    <div>
      <PageHeader title="Profil Saya" subtitle="Informasi akun & keanggotaan di ANGKASA STORE" />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Kartu ringkas */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 text-center">
          <div className="relative w-28 h-28 mx-auto">
            <div className="w-28 h-28 rounded-full overflow-hidden bg-sky-100 flex items-center justify-center ring-4 ring-sky-50">
              {img ? <img src={img} alt="foto" className="w-full h-full object-cover" data-testid="profile-photo" />
                : <span className="text-4xl font-semibold text-sky-600">{me.name?.[0]?.toUpperCase()}</span>}
            </div>
            <button onClick={() => fileRef.current?.click()} data-testid="profile-photo-btn"
              className="absolute bottom-0 right-0 w-9 h-9 rounded-full bg-sky-600 text-white flex items-center justify-center shadow hover:bg-sky-700">
              <Camera className="w-4 h-4" />
            </button>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="profile-photo-input" />
          </div>
          <h3 className="mt-4 text-lg font-semibold text-slate-900">{me.name}</h3>
          <span className="inline-flex items-center gap-1 mt-1 px-3 py-1 rounded-full text-xs font-medium bg-sky-100 text-sky-700 border border-sky-200">
            <BadgeCheck className="w-3.5 h-3.5" /> {roleLabel[me.role] || me.role}
          </span>
          <div className="mt-5 space-y-2 text-sm text-left">
            <div className="flex items-center gap-2 text-slate-600"><Building2 className="w-4 h-4 text-sky-500" /> {branchName}</div>
            <div className="flex items-center gap-2 text-slate-600"><Phone className="w-4 h-4 text-sky-500" /> {me.phone || "-"}</div>
            <div className="flex items-center gap-2 text-slate-600"><MapPin className="w-4 h-4 text-sky-500" /> {me.address || "-"}</div>
            <div className="flex items-center gap-2 text-slate-600"><CalendarDays className="w-4 h-4 text-sky-500" /> Masuk {joinDate ? fmtDate(joinDate) : "-"}</div>
          </div>
          <div className="mt-4 rounded-lg bg-gradient-to-br from-sky-600 to-sky-700 text-white p-3">
            <div className="text-[11px] uppercase tracking-wide opacity-90">Lama Bergabung</div>
            <div className="font-semibold" data-testid="profile-membership">{membership(joinDate)}</div>
          </div>
        </div>

        {/* Form edit */}
        <div className="lg:col-span-2 bg-white rounded-xl border border-slate-200 shadow-sm p-6">
          <h3 className="font-medium text-slate-800 mb-4">Detail Akun</h3>
          {!isPriv && <p className="text-xs text-amber-600 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2 mb-4">Sebagai Kasir, Anda hanya dapat mengubah foto & nomor HP. Jabatan dan tanggal masuk diatur oleh Staff/Owner.</p>}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs text-slate-500">Nama Lengkap</Label>
              <Input value={form.name || ""} disabled={!isPriv} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" data-testid="profile-name" />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Jabatan</Label>
              {isPriv ? (
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger className="mt-1" data-testid="profile-role"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="owner">Owner</SelectItem><SelectItem value="staff">Staff</SelectItem><SelectItem value="kasir">Kasir</SelectItem></SelectContent>
                </Select>
              ) : <Input value={roleLabel[form.role] || form.role || ""} disabled className="mt-1" />}
            </div>
            <div>
              <Label className="text-xs text-slate-500">Nomor HP</Label>
              <Input value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1" data-testid="profile-phone" />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Tanggal Masuk</Label>
              <Input type="date" value={(form.join_date || form.created_at || "").slice(0, 10)} disabled={!isPriv} onChange={(e) => setForm({ ...form, join_date: e.target.value })} className="mt-1" data-testid="profile-joindate" />
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs text-slate-500">Alamat</Label>
              <Input value={form.address || ""} disabled={!isPriv} onChange={(e) => setForm({ ...form, address: e.target.value })} className="mt-1" data-testid="profile-address" />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Email</Label>
              <Input value={me.email} disabled className="mt-1" />
            </div>
          </div>
          <Button onClick={save} disabled={saving} className="mt-5 bg-sky-600 hover:bg-sky-700" data-testid="profile-save-btn">{saving ? "Menyimpan..." : "Simpan Perubahan"}</Button>
        </div>
      </div>
    </div>
  );
}
